import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Chip,
  IconButton,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import Header from "./Header";
import Footer from "./Footer";
import { sslcPdfs } from "../assets/pdfData/sslc";
import { pucPdfs } from "../assets/pdfData/puc";
import { ugPdfs } from "../assets/pdfData/ug";
import { pgPdfs } from "../assets/pdfData/pg";
import { engineeringPdfs } from "../assets/pdfData/engineering";
import { examsPdfs } from "../assets/pdfData/exams";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import appScreenshot from "../assets/image.png";
import { keyframes } from "@mui/system";

const courseTree = {
  sslc: {
    label: "🧑‍🎓 SSLC / Class 10",
    levels: {
      "State Board": {
        subjects: ["Kannada", "English", "Maths", "Science", "Social", "Hindi"],
      },
      CBSE: {
        subjects: [
          "English",
          "Maths",
          "Science",
          "Social",
          "Hindi",
          "Computer",
        ],
      },
      ICSE: {
        subjects: [
          "English",
          "Maths",
          "Physics",
          "Chemistry",
          "Biology",
          "History",
        ],
      },
    },
  },
  puc: {
    label: "🎓 PUC / 11th-12th",
    levels: {
      Arts: {
        years: {
          "1st Year": [
            "History",
            "Political Science",
            "Economics",
            "Sociology",
            "English",
            "Kannada",
          ],
          "2nd Year": [
            "History",
            "Political Science",
            "Economics",
            "Sociology",
            "English",
            "Kannada",
          ],
        },
      },
      Commerce: {
        years: {
          "1st Year": [
            "Accounts",
            "Business",
            "Economics",
            "Statistics",
            "English",
            "Kannada",
          ],
          "2nd Year": [
            "Accounts",
            "Business",
            "Economics",
            "Statistics",
            "English",
            "Kannada",
          ],
        },
      },
      Science: {
        years: {
          "1st Year": [
            "Physics",
            "Chemistry",
            "Maths",
            "Biology",
            "Computer",
            "English",
          ],
          "2nd Year": [
            "Physics",
            "Chemistry",
            "Maths",
            "Biology",
            "Computer",
            "English",
          ],
        },
      },
    },
  },
  ug: {
    label: "🎓 UG Degrees",
    levels: {
      BBA: {
        semesters: {
          "Sem 1": [
            "Financial Accounting",
            "Business Maths",
            "Economics",
            "Management",
          ],
          "Sem 2": ["Cost Accounting", "Statistics", "Marketing", "HRM"],
          "Sem 3": [],
          "Sem 4": [],
          "Sem 5": [],
          "Sem 6": [],
        },
      },
      BCA: {
        semesters: {
          "Sem 1": ["C Programming", "Maths", "Digital Logic", "DBMS"],
          "Sem 2": ["Data Structures", "OOPS", "OS"],
          "Sem 3": [],
          "Sem 4": [],
          "Sem 5": [],
          "Sem 6": [],
        },
      },
    },
  },
  pg: {
    label: "🎓 PG Degrees",
    levels: {
      MBA: {
        semesters: {
          "Sem 1": ["Marketing", "Finance", "HR", "Operations"],
          "Sem 2": ["Strategy", "Analytics"],
          "Sem 3": [],
          "Sem 4": [],
        },
      },
      MCA: {
        semesters: {
          "Sem 1": ["Advanced Java", "Cloud Computing"],
          "Sem 2": ["Big Data", "AI/ML"],
          "Sem 3": [],
          "Sem 4": [],
        },
      },
    },
  },
  engineering: {
    label: "🏗️ Engineering",
    levels: {
      "B.E / B.Tech": {
        semesters: {
          "Sem 1": [
            "Maths",
            "Physics",
            "Chemistry",
            "C Programming",
            "EVS",
            "English",
          ],
          "Sem 2": [
            "Maths 2",
            "Physics",
            "Electrical",
            "Mechanics",
            "CAED",
            "English",
          ],
          "Sem 3": [],
          "Sem 4": [],
          "Sem 5": [],
          "Sem 6": [],
          "Sem 7": [],
        },
      },
    },
  },
  exams: {
    label: "📝 Entrance Exams",
    levels: {
      JEE: { subjects: ["Maths", "Physics", "Chemistry"] },
      NEET: { subjects: ["Physics", "Chemistry", "Biology"] },
      KCET: { subjects: ["Maths", "Physics", "Chemistry"] },
    },
  },
};

const TABS = ["Syllabus", "Notes", "Papers"];
const TAB_KEYS = { 0: "syllabus", 1: "notes", 2: "papers" };

const pdfDatabase = {
  sslc: sslcPdfs,
  puc: pucPdfs,
  ug: ugPdfs,
  pg: pgPdfs,
  engineering: engineeringPdfs,
  exams: examsPdfs,
};

export default function Categories() {
  const [category, setCategory] = useState("sslc");
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [pdfData, setPdfData] = useState([]);
  const [bounce, setBounce] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setBounce(true);
      setTimeout(() => setBounce(false), 600);
    }, 2000);

    return () => clearInterval(interval);
  }, []);
  const [favorites, setFavorites] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(localStorage.getItem("favorites") || "[]");
    } catch {
      return [];
    }
  });
  const successCheck = keyframes`
  0% { opacity: 0; transform: scale(0) rotate(-12deg); }
  50% { opacity: 1; transform: scale(1.2) rotate(4deg); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
`;

  const currentCategory = courseTree[category] || {
    label: "Select Category",
    levels: {},
  };
  const levels = Object.keys(currentCategory.levels || {});
  const levelData = selectedLevel
    ? (currentCategory.levels || {})[selectedLevel]
    : {};
  const yearsOrSemesters = Object.keys(
    levelData.years || levelData.semesters || {}
  );
  const subjects =
    selectedLevel && selectedYear
      ? levelData.years?.[selectedYear] ||
      levelData.semesters?.[selectedYear] ||
      []
      : levelData.subjects || [];

  useEffect(() => {
    if (selectedSubject && selectedLevel) {
      const tabKey = TAB_KEYS[activeTab];
      let pdfs =
        pdfDatabase[category]?.[selectedLevel]?.[selectedYear]?.[
        selectedSubject
        ]?.[tabKey];
      if (!pdfs)
        pdfs =
          pdfDatabase[category]?.[selectedLevel]?.[selectedSubject]?.[tabKey];
      setPdfData(pdfs || []);
    } else {
      setPdfData([]);
    }
  }, [category, selectedLevel, selectedYear, selectedSubject, activeTab]);

  const handleChangeCategory = (value) => {
    setCategory(value);
    setSelectedLevel(null);
    setSelectedYear(null);
    setSelectedSubject(null);
    setActiveTab(0);
    setPdfData([]);
  };

  const handleSelectLevel = (level) => {
    setSelectedLevel(level);
    setSelectedYear(null);
    setSelectedSubject(null);
    setActiveTab(0);
    setPdfData([]);
  };

  const handleSelectYear = (year) => {
    setSelectedYear(year);
    setSelectedSubject(null);
    setActiveTab(0);
    setPdfData([]);
  };

  const handleSelectSubject = (subject) => {
    setSelectedSubject(subject);
    setActiveTab(0);
  };

  const handleToggleFavorite = (pdf) => {
    const exists = favorites.some((f) => f.id === pdf.id);
    if (exists) {
      const next = favorites.filter((f) => f.id !== pdf.id);
      syncFavorites(next);
    } else {
      const favItem = {
        ...pdf,
        category,
        level: selectedLevel,
        yearOrSem: selectedYear,
        subject: selectedSubject,
        tab: TAB_KEYS[activeTab],
      };
      const next = [...favorites, favItem];
      syncFavorites(next);
    }
  };

  const syncFavorites = (next) => {
    setFavorites(next);
    localStorage.setItem("favorites", JSON.stringify(next));
  };

  const handleOpenPdf = (pdfUrl) => window.open(pdfUrl, "_blank");

  const getStepLabel = () => {
    if (!selectedLevel) return "1️⃣ Level";
    if (yearsOrSemesters.length > 0 && !selectedYear) return "2️⃣ Year/Semester";
    if (subjects.length > 0 && !selectedSubject) return "3️⃣ Subject";
    return "4️⃣ Content";
  };

  const renderPdfSection = () => {
    if (!selectedSubject) {
      return (
        <Typography
          sx={{
            mt: 3,
            color: "#777",
            textAlign: "center",
            fontStyle: "italic",
          }}
        ></Typography>
      );
    }

    return (
      <Box mt={4}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography variant="h5" fontWeight="bold">
            {selectedSubject}
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          centered
          variant="fullWidth"
          sx={{
            mb: 3,
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: 3,
              background:
                "linear-gradient(90deg, #2563EB 0%, #10B981 50%, #F97316 100%)",
            },
            "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
          }}
        >
          {TABS.map((tab, index) => {
            const count = favorites.filter(
              (pdf) =>
                pdf.category === category &&
                (!pdf.tab || pdf.tab === TAB_KEYS[index])
            ).length;
            return (
              <Tab
                key={index}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {tab}
                    <Chip label={`(${count})`} size="small" />
                  </Box>
                }
              />
            );
          })}
        </Tabs>

        {pdfData.length > 0 ? (
          <Grid container spacing={2}>
            {pdfData.map((pdf) => {
              const isFav = favorites.some((f) => f.id === pdf.id);
              return (
                <Grid size={{ xs: 12, sm: 6, md: 6 }} key={pdf.id}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      // p: 3,
                      // textAlign: "center",
                      borderRadius: 5,
                      boxShadow: 12,

                      borderLeft: "4px solid #1A4B83",
                      borderRight: "4px solid #1A4B83",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        pb: 2,
                        bgcolor: "#F8FAFC",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={2}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleOpenPdf(pdf.fileUrl)}
                      >
                        <PictureAsPdfIcon
                          sx={{ fontSize: 23, color: "#DC2626", mt: 0.5 }}
                        />
                        <Box flex={1}>
                          <Typography
                            fontWeight="bold"
                            noWrap
                            sx={{ mb: 0.5, fontSize: { xs: 16, md: 17 } }}
                          >
                            {pdf.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {pdf.size} • {pdf.pages} pages
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(pdf);
                          }}
                          size="small"
                        >
                          {isFav ? (
                            <FavoriteIcon sx={{ color: "red" }} />
                          ) : (
                            <FavoriteBorderIcon sx={{ color: "red" }} />
                          )}
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box
            sx={{
              mt: 4,
              p: { xs: 3, md: 4 },
              borderRadius: 10,
              background:
                "linear-gradient(135deg, #1A4B83 0%, #3B82F6 50%, #10B981 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
          >
            <a
              href="https://play.google.com/store/apps/details?id=com.getmyrank&hl=en_IN"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", display: "block" }}
            >
              <Box
                component="img"
                src={appScreenshot}
                alt="GetMyRank App"
                sx={{
                  width: { xs: 300, md: 450 },
                  height: "auto",
                  maxHeight: 250,
                  borderRadius: 8,
                  mb: 3,
                }}
              />

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#fff", mb: 1, fontSize: { xs: 20, md: 24 } }}
              >
                Download GetMyRank App
              </Typography>

              <Typography sx={{ color: "#E2E8F0", mb: 3, fontSize: 15 }}>
                📚 Syllabus • Notes • Papers
              </Typography>

              {/* Button with Right Arrow */}
              <Box
                sx={{
                  px: 4,
                  py: 2.5,
                  mx: "auto",
                  bgcolor: "#fff",
                  borderRadius: 50,
                  fontWeight: 600,
                  fontSize: 16,
                  color: "#1A4B83",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#FCD34D",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
                  },
                }}
              >
                <Box
                  sx={{
                    fontSize: 20,
                    animation: bounce ? "bounceRight 0.6s ease-in-out" : "none",
                    transformOrigin: "center",
                  }}
                >
                  ➡️
                </Box>
                <span>📱 Get on Play Store</span>

                {/* Right Pointing Arrow - Bounces every 2s */}
              </Box>
            </a>

            <style jsx>{`
              @keyframes bounceRight {
                0%,
                100% {
                  transform: translateX(0) scale(1);
                }
                50% {
                  transform: translateX(6px) scale(1.1);
                }
              }
            `}</style>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          p: { xs: 2, md: 5 },
          minHeight: "85vh",
          bgcolor: "#F9FAFB",
        }}
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 2.5, sm: 3 }}>
            <Typography sx={{
              fontSize: 25, fontWeight: "bold", mb: 3, borderBottom: "3px solid #000",

              width: "fit-content",
            }}>
              📚 Categories
            </Typography>
            <RadioGroup
              value={category}
              onChange={(e) => handleChangeCategory(e.target.value)}
            >
              {Object.entries(courseTree).map(([key, value]) => (
                <FormControlLabel
                  key={key}
                  value={key}
                  control={<Radio size="small" />}
                  label={value.label}
                  sx={{ mb: 1.5, fontWeight: "bold" }}
                />
              ))}
            </RadioGroup>
          </Grid>

          <Grid
            size={{ xs: 0, md: 0.5 }}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: "100%", width: 2, bgcolor: "#E2E8F0" }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 9, sm: 8 }}>
            <Typography sx={{ fontSize: 25, fontWeight: "bold", mb: 3 }}>
              {currentCategory.label}
            </Typography>
            <Typography variant="h6" color="text.secondary" mb={4}>
              {getStepLabel()}
            </Typography>

            {!selectedLevel && (
              <>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Select Board / Stream / Course / Exam
                </Typography>
                <Grid container spacing={2} mb={4}>
                  {levels.map((level) => (
                    <Grid size={{ xs: 12, md: 4, sm: 4 }} key={level}>
                      <Card
                        onClick={() => handleSelectLevel(level)}
                        sx={{
                          cursor: "pointer",
                          p: 3,
                          textAlign: "center",
                          borderRadius: 5,
                          boxShadow: 12,

                          borderLeft: "4px solid #1A4B83",
                          borderRight: "4px solid #1A4B83",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography fontWeight="bold" fontSize={16}>
                            {level}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {selectedLevel && !selectedYear && yearsOrSemesters.length > 0 && (
              <>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Select Year / Semester
                </Typography>
                <Grid container spacing={2} mb={4}>
                  {yearsOrSemesters.map((year) => (
                    <Grid size={{ xs: 6, md: 3, sm: 4 }} key={year}>
                      <Card
                        onClick={() => handleSelectYear(year)}
                        sx={{
                          cursor: "pointer",
                          p: 3,
                          textAlign: "center",
                          borderRadius: 5,
                          boxShadow: 12,

                          borderLeft: "4px solid #1A4B83",
                          borderRight: "4px solid #1A4B83",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography fontWeight="bold" fontSize={15}>
                            {year}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {subjects.length > 0 && !selectedSubject && (
              <>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Select Subject
                </Typography>
                <Grid container spacing={2} mb={4}>
                  {subjects.map((subject) => (
                    <Grid size={{ xs: 6, md: 4, sm: 4 }} key={subject}>
                      <Card
                        onClick={() => handleSelectSubject(subject)}
                        sx={{
                          cursor: "pointer",
                          p: 3,
                          textAlign: "center",
                          borderRadius: 5,
                          boxShadow: 12,

                          borderLeft: "4px solid #1A4B83",
                          borderRight: "4px solid #1A4B83",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography fontWeight="bold" fontSize={16}>
                            {subject}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {renderPdfSection()}
          </Grid>
        </Grid>
      </Box>
      <Footer />
    </>
  );
}

